<template>
  <div ref="containerRef" class="scroll-container" @scroll="handleScroll">
    <!-- Initial Loader (when first fetching data) -->
    <v-row v-if="isLoading">
      <ProgressLinearLoaderVue :isLoading="isLoading" />
    </v-row>

    <!-- List of VAV Cards -->
    <v-row>
      <v-col v-for="vav in tempItems" :key="vav.deviceId">
        <VAVCard
          v-if="getDeviceFieldMappings(vav.deviceId)"
          :VAVInfo="vav"
          :fieldMappings="getDeviceFieldMappings(vav.deviceId)"
          :layoutConfig="getDeviceLayoutConfig(vav.deviceId)"
        />
        <!-- A device with readings but no field mappings has nothing to
             render with; say so instead of dropping it from the page. -->
        <v-card
          v-else
          variant="outlined"
          width="360"
          class="pa-4 unmapped-device"
          data-cy="unmapped-device"
        >
          <div class="text-subtitle-1 font-weight-bold text-primary">
            {{ vav.friendlyName || vav.deviceName }}
          </div>
          <div class="text-caption text-sonicSilver">
            {{ vav.siteLocationName }}
          </div>
          <div class="text-body-2 text-primary mt-2">
            No field mappings are configured for this VAV yet, so its
            readings cannot be shown.
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Spinner when loading more items -->
    <v-row v-if="isFetchingMore" class="loader-container">
      <v-progress-circular indeterminate color="primary" />
    </v-row>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { searchByTermFactory } from '@/Core.Patterns/Factory';
import { sortAlphabetically } from '@/Core.Patterns/Strategy';

const VAVCard = defineAsyncComponent(
  () => import('@/Core.Service.Domain/Controls/VAVs/Components/VAVCard.vue')
);

import {
  IVAV,
  IVAVMapping,
  VAVLayoutContract,
  DeviceLayoutConfig,
} from '@/Core.Service.Domain/Controls/VAVs/types/VAV.ts';
import API from '@/Core.Service.Domain/Controls/VAVs/API/index.ts';
import defaultVAVLayout from '@/Core.Service.Domain/Controls/VAVs/API/VAV.json';
import ProgressLinearLoaderVue from '@/Core.UI.Domain/Components/ProgressLinearLoader.vue';

const VAVsList = ref<IVAV[]>([]);
const isLoading = ref(true);
const isFetchingMore = ref(false);

const emits = defineEmits(['update:VAVsList', 'update:isLoading']);
const store = useStore();
const defaultSite = computed(() => store.getters['session/defaultSite']);
const controlCurrentRoute = computed(
  () => store.getters['ControlsStore/controlCurrentRoute']
);
const route = useRoute();

const updateDeleteVAV = computed(() => {
  return store.getters['ControlsStore/VAVsStore/getDeleteVAV'];
});

const searchTerm = computed(
  () => store.getters['ControlsStore/getControlSearchTerm']
);

const filteredItems = ref<IVAV[]>([]);
const visibleItems = ref<IVAV[]>([]);
const containerRef = ref<HTMLElement | null>(null);

const chunkSize = 10;

const updateVisibleItems = async () => {
  if (visibleItems.value.length >= filteredItems.value.length) return;

  isFetchingMore.value = true;
  await new Promise((resolve) => setTimeout(resolve, 500));

  const nextBatch = filteredItems.value.slice(
    visibleItems.value.length,
    visibleItems.value.length + chunkSize
  );

  visibleItems.value = [...visibleItems.value, ...nextBatch];
  isFetchingMore.value = false;
};

const tempItems = computed(() => {
  return visibleItems.value.length ? visibleItems.value : filteredItems.value;
});

const handleScroll = () => {
  if (!containerRef.value || isFetchingMore.value) return;

  const { scrollTop, clientHeight, scrollHeight } = containerRef.value;

  if (scrollTop + clientHeight >= scrollHeight - 150) {
    updateVisibleItems();
  }
};

const selectedSiteControls = computed<IVAV[]>(() => {
  return store.getters['ControlsStore/selectedSiteControls'];
});

const interval = 120000;
const intervalId = ref<ReturnType<typeof setInterval> | null>(null);

onMounted(() => {
  intervalId.value = setInterval(() => {
    loadVavs();
  }, interval);
});

onBeforeUnmount(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});

// Load VAVs
const loadVavs = async () => {
  isLoading.value = true;
  emits('update:isLoading', isLoading.value);
  try {
    const vavsLatestReadings = await API.getVAVsBySiteId(defaultSite.value.id);

    const cachedVAVs = JSON.parse(
      localStorage.getItem('local-storage-vavs') || '[]'
    );

    const filteredVAVs = cachedVAVs.filter((vav: IVAV) => {
      const latestVav = vavsLatestReadings.find(
        (latestVav: IVAV) => latestVav.deviceId === vav.deviceId
      );
      if (latestVav) {
        return vav.usageDateTime >= latestVav.usageDateTime;
      }
      return false;
    });

    localStorage.setItem('local-storage-vavs', JSON.stringify(filteredVAVs));

    if (filteredVAVs.length > 0) {
      const mergedVAVs = vavsLatestReadings.map((vav: IVAV) => {
        const cached = filteredVAVs.find(
          (f: IVAV) => f.deviceId === vav.deviceId
        );
        return cached ? { ...vav, ...cached } : vav;
      });
      VAVsList.value = mergedVAVs;
    } else {
      VAVsList.value = vavsLatestReadings;
    }
    await nextTick();
    VAVsList.value = VAVsList.value.map((vav) => {
      const isSelected = selectedSiteControls.value.find((site) => {
        return site.id === vav.deviceId;
      });
      return { ...vav, ...isSelected };
    });
    VAVsList.value = VAVsList.value.filter((vav) => {
      return vav.isIgnored === false;
    });

    filteredItems.value = [...VAVsList.value];
    visibleItems.value = [];
    emits('update:VAVsList', VAVsList.value.length);
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
    emits('update:isLoading', isLoading.value);
    filteredItems.value = [...VAVsList.value];
    visibleItems.value = [];
    updateVisibleItems();
  }
};

const mappings = ref<IVAVMapping[]>([]);

// Every card renders the bundled VAV.json layout: the API no longer serves
// per-site layouts (its /sites/{siteId}/layout endpoint was removed).
const siteLayout = defaultVAVLayout as VAVLayoutContract;

const mapDynamicFields = async () => {
  try {
    mappings.value = await API.getVAVMappings(defaultSite.value.id);
  } catch (mappingError) {
    console.error(
      'Failed to fetch mappings, using default layout:',
      mappingError
    );
    mappings.value = [];
  }
};

const mappingsByDevice = computed<Record<number, IVAVMapping['mappings']>>(
  () => {
    const map: Record<number, IVAVMapping['mappings']> = {};
    // Ensure mappings.value is an array before iterating
    const mappingsArray = Array.isArray(mappings.value) ? mappings.value : [];
    for (const m of mappingsArray) {
      if (m && m.deviceId && m.mappings) {
        map[m.deviceId] = m.mappings;
      }
    }
    return map;
  }
);

const getDeviceFieldMappings = (deviceId: number) => {
  return mappingsByDevice.value[deviceId] || null;
};

const getDeviceLayoutConfig = (deviceId: number): DeviceLayoutConfig => {
  return {
    layout: siteLayout,
    mappings: getDeviceFieldMappings(deviceId),
  };
};

watch(
  [selectedSiteControls, controlCurrentRoute],
  async ([newValue, currentRoute]) => {
    if (!newValue.length) {
      // Once the controls bar has finished loading this route and found no
      // VAVs for the site, settle the loading state so the parent can show
      // its empty banner instead of an endless progress bar.
      if (currentRoute === route.name) {
        VAVsList.value = [];
        filteredItems.value = [];
        visibleItems.value = [];
        isLoading.value = false;
        emits('update:isLoading', false);
        emits('update:VAVsList', 0);
      }
      return;
    }
    if (newValue.length) {
      await loadVavs();
      // Fire-and-forget mappings; don't block initial VAV rendering
      mapDynamicFields();
      updateVisibleItems();
      await nextTick();
      if (containerRef.value) {
        containerRef.value.addEventListener('scroll', handleScroll);
      }
    }
  },
  { immediate: true }
);

watch(searchTerm, () => {
  filteredItems.value = searchByTermFactory(
    searchTerm.value,
    ['deviceName', 'friendlyName', 'deviceId', 'siteLocationName'],
    VAVsList.value
  );
  visibleItems.value = [];
  updateVisibleItems();
});

watch(VAVsList, () => {
  filteredItems.value = sortAlphabetically(VAVsList.value, 'friendlyName');
  visibleItems.value = [];
  updateVisibleItems();
});

watch(defaultSite, () => {
  loadVavs();
  mapDynamicFields();
});

watch(updateDeleteVAV, () => {
  if (updateDeleteVAV.value) {
    store.commit('ControlsStore/VAVsStore/SET_VAV_UPDATE_DELETE', false);
    loadVavs();
  }
});
</script>

<style lang="scss" scoped>
.scroll-container {
  height: 100vh;
  width: 100%;
  overflow-y: auto;
  padding: 102px 50px;
  margin-left: -50px;
  margin-top: -100px;
  scrollbar-width: none;
}

.loader-container {
  display: flex;
  justify-content: center;
  margin-top: -20%;
}

.v-col {
  flex-grow: 0;
}
</style>
